import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  FaArrowLeft, FaPlus, FaPaperPlane, FaRedo, FaTrash, FaEdit,
  FaSpinner, FaCheckCircle, FaTimesCircle, FaClock, FaEnvelope,
  FaUserTie, FaSchool, FaChartBar, FaEye
} from 'react-icons/fa';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import api from '../utils/api';

const STATUS_LABELS = {
  new: { label: 'Nouveau', color: 'bg-gray-500', icon: FaClock },
  contacted: { label: 'Contacté', color: 'bg-blue-500', icon: FaEnvelope },
  interested: { label: 'Intéressé', color: 'bg-yellow-500', icon: FaUserTie },
  converted: { label: 'Converti', color: 'bg-green-500', icon: FaCheckCircle },
  refused: { label: 'Refusé', color: 'bg-red-500', icon: FaTimesCircle }
};

const ProspectionAdminPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [prospects, setProspects] = useState([]);
  const [stats, setStats] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [sendingId, setSendingId] = useState(null);
  const [filter, setFilter] = useState('all');
  
  const [newProspect, setNewProspect] = useState({
    email: '',
    name: '',
    school_name: '',
    template: 'generic',
    is_tutoyement: false,
    notes: ''
  });

  useEffect(() => {
    fetchProspects();
    fetchStats();
  }, []);

  const fetchProspects = async () => {
    try {
      const response = await api.get('/prospection/');
      setProspects(response.data.prospects || []);
    } catch (error) {
      console.error('Erreur chargement prospects:', error);
      toast.error('Erreur lors du chargement des prospects');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/prospection/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Erreur chargement stats:', error);
    }
  };

  const handleAddProspect = async (e) => {
    e.preventDefault();
    try {
      await api.post('/prospection/', newProspect);
      toast.success('Prospect ajouté !');
      setShowAddForm(false);
      setNewProspect({ email: '', name: '', school_name: '', template: 'generic', is_tutoyement: false, notes: '' });
      fetchProspects();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'ajout');
    }
  };

  const handleSendInitial = async (prospectId) => {
    setSendingId(prospectId);
    try {
      await api.post(`/prospection/${prospectId}/send-initial`);
      toast.success('Email initial envoyé !');
      fetchProspects();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSendingId(null);
    }
  };

  const handleSendFollowup = async (prospectId) => {
    setSendingId(prospectId);
    try {
      await api.post(`/prospection/${prospectId}/send-followup`);
      toast.success('Relance envoyée !');
      fetchProspects();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur ou toutes relances déjà envoyées');
    } finally {
      setSendingId(null);
    }
  };

  const handleUpdateStatus = async (prospectId, newStatus) => {
    try {
      await api.patch(`/prospection/${prospectId}`, { status: newStatus });
      toast.success('Statut mis à jour !');
      fetchProspects();
      fetchStats();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDelete = async (prospectId) => {
    if (!window.confirm('Supprimer ce prospect ?')) return;
    try {
      await api.delete(`/prospection/${prospectId}`);
      toast.success('Prospect supprimé');
      fetchProspects();
      fetchStats();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleRunAllFollowups = async () => {
    try {
      const response = await api.post('/prospection/run-followups');
      toast.success(`Relances exécutées: ${JSON.stringify(response.data.stats)}`);
      fetchProspects();
      fetchStats();
    } catch (error) {
      toast.error('Erreur lors des relances automatiques');
    }
  };

  const filteredProspects = filter === 'all' 
    ? prospects 
    : prospects.filter(p => p.status === filter);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', { 
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <FaSpinner className="animate-spin text-4xl text-[#1B4965]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#1B4965] to-[#2D5F7C] text-white py-6 px-6">
        <div className="max-w-7xl mx-auto">
          <button 
            onClick={() => navigate('/admin')}
            className="flex items-center gap-2 text-white/80 hover:text-white mb-4"
          >
            <FaArrowLeft /> Retour au dashboard
          </button>
          <h1 className="text-3xl font-bold">Gestion de la Prospection</h1>
          <p className="text-white/70 mt-1">Gérez vos prospects et campagnes d'emailing</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <Card className="bg-white">
              <CardContent className="p-4 text-center">
                <div className="text-3xl font-bold text-[#1B4965]">{stats.total || 0}</div>
                <div className="text-sm text-gray-500">Total prospects</div>
              </CardContent>
            </Card>
            {Object.entries(STATUS_LABELS).map(([key, { label, color }]) => (
              <Card key={key} className="bg-white">
                <CardContent className="p-4 text-center">
                  <div className="text-2xl font-bold">{stats[key] || 0}</div>
                  <div className={`text-xs px-2 py-1 rounded-full text-white ${color} inline-block mt-1`}>
                    {label}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-wrap gap-3 mb-6">
          <Button 
            onClick={() => setShowAddForm(true)}
            className="bg-[#1B4965] hover:bg-[#163a52]"
          >
            <FaPlus className="mr-2" /> Nouveau prospect
          </Button>
          <Button 
            onClick={handleRunAllFollowups}
            variant="outline"
          >
            <FaRedo className="mr-2" /> Exécuter les relances auto
          </Button>
          
          {/* Filtres */}
          <div className="flex gap-2 ml-auto">
            <select 
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="border rounded-lg px-3 py-2 text-sm"
            >
              <option value="all">Tous les statuts</option>
              {Object.entries(STATUS_LABELS).map(([key, { label }]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Add Form Modal */}
        {showAddForm && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          >
            <Card className="w-full max-w-md">
              <CardHeader>
                <CardTitle>Ajouter un prospect</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleAddProspect} className="space-y-4">
                  <div>
                    <Label>Email *</Label>
                    <Input 
                      type="email"
                      value={newProspect.email}
                      onChange={(e) => setNewProspect({...newProspect, email: e.target.value})}
                      required
                      placeholder="directeur@ecole.fr"
                    />
                  </div>
                  <div>
                    <Label>Nom du contact *</Label>
                    <Input 
                      value={newProspect.name}
                      onChange={(e) => setNewProspect({...newProspect, name: e.target.value})}
                      required
                      placeholder="Jean Dupont"
                    />
                  </div>
                  <div>
                    <Label>Nom de l'école</Label>
                    <Input 
                      value={newProspect.school_name}
                      onChange={(e) => setNewProspect({...newProspect, school_name: e.target.value})}
                      placeholder="ISCPA Lyon"
                    />
                  </div>
                  <div>
                    <Label>Template d'email</Label>
                    <select 
                      value={newProspect.template}
                      onChange={(e) => setNewProspect({...newProspect, template: e.target.value})}
                      className="w-full border rounded-lg px-3 py-2"
                    >
                      <option value="generic">Générique</option>
                      <option value="iscpa">ISCPA</option>
                      <option value="esjc_niger">ESJC Niger</option>
                      <option value="ildj">ILDJ</option>
                    </select>
                  </div>
                  <div className="flex items-center gap-2">
                    <input 
                      type="checkbox"
                      id="tutoyement"
                      checked={newProspect.is_tutoyement}
                      onChange={(e) => setNewProspect({...newProspect, is_tutoyement: e.target.checked})}
                    />
                    <Label htmlFor="tutoyement">Tutoiement</Label>
                  </div>
                  <div>
                    <Label>Notes</Label>
                    <textarea 
                      value={newProspect.notes}
                      onChange={(e) => setNewProspect({...newProspect, notes: e.target.value})}
                      className="w-full border rounded-lg px-3 py-2 h-20"
                      placeholder="Notes internes..."
                    />
                  </div>
                  <div className="flex gap-3 pt-4">
                    <Button type="submit" className="flex-1 bg-[#1B4965]">
                      Ajouter
                    </Button>
                    <Button type="button" variant="outline" onClick={() => setShowAddForm(false)}>
                      Annuler
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Prospects List */}
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left p-4 font-semibold">Prospect</th>
                    <th className="text-left p-4 font-semibold">École</th>
                    <th className="text-left p-4 font-semibold">Statut</th>
                    <th className="text-left p-4 font-semibold">Emails</th>
                    <th className="text-left p-4 font-semibold">Dernière action</th>
                    <th className="text-right p-4 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProspects.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="text-center p-8 text-gray-500">
                        Aucun prospect trouvé
                      </td>
                    </tr>
                  ) : (
                    filteredProspects.map((prospect) => {
                      const statusInfo = STATUS_LABELS[prospect.status] || STATUS_LABELS.new;
                      const StatusIcon = statusInfo.icon;
                      const emailsSent = prospect.emails_sent?.length || 0;
                      
                      return (
                        <tr key={prospect.id} className="border-b hover:bg-gray-50">
                          <td className="p-4">
                            <div className="font-medium">{prospect.name}</div>
                            <div className="text-sm text-gray-500">{prospect.email}</div>
                          </td>
                          <td className="p-4">
                            <div className="flex items-center gap-2">
                              <FaSchool className="text-gray-400" />
                              {prospect.school_name || '-'}
                            </div>
                          </td>
                          <td className="p-4">
                            <select
                              value={prospect.status || 'new'}
                              onChange={(e) => handleUpdateStatus(prospect.id, e.target.value)}
                              className={`text-white text-xs px-2 py-1 rounded-full ${statusInfo.color} border-0 cursor-pointer`}
                            >
                              {Object.entries(STATUS_LABELS).map(([key, { label }]) => (
                                <option key={key} value={key} className="text-black bg-white">
                                  {label}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="p-4">
                            <div className="flex items-center gap-1">
                              <FaEnvelope className="text-gray-400" />
                              <span className="font-medium">{emailsSent}</span>
                              <span className="text-gray-500 text-sm">/ 4</span>
                            </div>
                            {prospect.followup_count > 0 && (
                              <div className="text-xs text-gray-500">
                                {prospect.followup_count} relance(s)
                              </div>
                            )}
                          </td>
                          <td className="p-4 text-sm text-gray-500">
                            {formatDate(prospect.last_email_sent || prospect.created_at)}
                          </td>
                          <td className="p-4">
                            <div className="flex justify-end gap-2">
                              {emailsSent === 0 ? (
                                <Button
                                  size="sm"
                                  onClick={() => handleSendInitial(prospect.id)}
                                  disabled={sendingId === prospect.id}
                                  className="bg-green-600 hover:bg-green-700"
                                >
                                  {sendingId === prospect.id ? (
                                    <FaSpinner className="animate-spin" />
                                  ) : (
                                    <>
                                      <FaPaperPlane className="mr-1" /> Envoyer
                                    </>
                                  )}
                                </Button>
                              ) : (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleSendFollowup(prospect.id)}
                                  disabled={sendingId === prospect.id || emailsSent >= 4}
                                >
                                  {sendingId === prospect.id ? (
                                    <FaSpinner className="animate-spin" />
                                  ) : (
                                    <>
                                      <FaRedo className="mr-1" /> Relancer
                                    </>
                                  )}
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleDelete(prospect.id)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <FaTrash />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ProspectionAdminPage;
